import React from 'react';
import {
  ProjectsIcon,
  DiverseIcon,
  FilesIcon,
  SharedWithMeIcon,
  SettingsIcon,
  UpgradeToProIcon,
  UpgradeToBusinessIcon,
  ArrowRight,
} from 'icons';
import { isNil } from 'lodash';
import { routerStore } from 'core/routing';
import { isProfileSlug, isInitiativeSlug } from 'utils';
import { clientStore } from 'apps/client/app';
import { sharedWithMeRoutes } from 'apps/routes/utils';
import { Icon } from 'components';
import { themes } from 'styles/themes/wikifactoryNew';

type StatisticsItem = {
  totalCount: number;
};

type SpaceStatistics = {
  sharedFiles: StatisticsItem;
  projects: StatisticsItem;
  rfqQuotesOrders: StatisticsItem;
};

const MenuTitle = ({
  text,
  icon,
  totalCount,
  className,
}: {
  text: string;
  totalCount?: number;
  icon?: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={`text-black flex items-center ${className}`}>
      {text}{' '}
      {!isNil(totalCount) && (
        <span className='text-gray-600'>&nbsp;{`(${totalCount})`}</span>
      )}
      &nbsp;&nbsp;{icon}
    </div>
  );
};

const commonMenuItemStyle: React.CSSProperties = {
  display: 'flex',
  padding: 20,
  paddingLeft: 24,
  paddingRight: 12,
  height: 36,
  gap: 4,
  alignSelf: 'stretch',
};

export const getCommonMenus = ({
  statistics,
  spacePath,
  isCurrentSpace,
}: {
  spacePath?: string;
  isCurrentSpace: boolean;
  statistics: SpaceStatistics;
}) => {
  const { navigate, route } = routerStore;
  const isActiveProfile = isProfileSlug(spacePath);
  const spacePrefix = isActiveProfile ? 'My' : '';
  const { sharedFiles, projects, rfqQuotesOrders } = statistics || {};
  const isIntermediator = clientStore.currentPortal === 'INTERMEDIATOR';

  const isRFQQuotesOrders =
    isCurrentSpace &&
    [
      'workspace.rfqs',
      'rfq',
      'workspace.intermediator.rfqs',
      'intermediator.rfq',
      'workspace.manufacturer.rfqs',
      'manufacturer.rfq',
      // quotes
      'workspace.quotes',
      'quote',
      'intermediator.quote',
      'quotes',
      'workspace.intermediator.quotes',
      // orders
      'workspace.orders',
      'order',
      'workspace.intermediator.orders',
      'intermediator.order',
      'workspace.manufacturer.orders',
      'manufacturer.order',
    ]
      .map(name => {
        return isIntermediator
          ? name.replace('workspace', 'workspace.intermediator')
          : name;
      })
      .includes(route.name);

  const isActiveFiles =
    isCurrentSpace && ['workspace.spaceFiles'].includes(route.name);

  const isActiveProjects =
    isCurrentSpace && route.name.includes('workspace.spaceProjects');

  const isBusinessPlan =
    clientStore.activeInitiativeSpace.subscriptionType === 'BUSINESS' &&
    !isActiveProfile;

  return [
    {
      name: 'workspace.spaceProjects',
      label: (
        <MenuTitle
          text={`${spacePrefix} Projects`}
          totalCount={projects?.totalCount}
          className={isActiveProjects ? 'font-bold' : ''}
        />
      ),
      key: 'workspace.spaceProjects',
      icon: (
        <ProjectsIcon
          style={{
            color: isActiveProjects
              ? themes.color.wfBlack
              : themes.color.wfGrey100,
          }}
        />
      ),
      'data-testid': isActiveProfile ? 'myProjects' : 'initiativeProjects',
      onClick: () => {
        if (spacePath) {
          navigate('workspace.spaceProjects', {
            spacePath,
          });
          clientStore.UpdateProfileOrTeamWorkspace();
        }
      },
      isActive: isActiveProjects,
    },
    {
      name: 'workspace.spaceFiles',
      label: (
        <MenuTitle
          text={`${spacePrefix} Files`}
          totalCount={sharedFiles?.totalCount}
          className={isActiveFiles ? 'font-bold' : ''}
        />
      ),
      key: 'workspace.spaceFiles',
      classes: {
        selected: {
          backgroundColor: 'red',
        },
      },
      icon: (
        <FilesIcon
          style={{
            color: isActiveFiles
              ? themes.color.wfBlack
              : themes.color.wfGrey100,
          }}
        />
      ),
      'data-testid': isActiveProfile ? 'myFiles' : 'initiativeFiles',
      onClick: () => {
        if (spacePath) {
          navigate('workspace.spaceFiles', {
            spacePath,
          });
          clientStore.UpdateProfileOrTeamWorkspace();
        }
      },
      isActive: isActiveFiles,
    },
    isBusinessPlan || clientStore.hasRole('INTERMEDIATOR') || CI === 'true'
      ? {
          name: 'workspace.rfqs',
          label: (
            <MenuTitle
              text={`${spacePrefix} RFQs, Quotes & Orders`}
              totalCount={rfqQuotesOrders?.totalCount}
              className={isRFQQuotesOrders ? 'font-bold' : ''}
            />
          ),
          key: 'workspace.rfqs',
          icon: (
            <DiverseIcon
              style={{
                color: isRFQQuotesOrders
                  ? themes.color.wfBlack
                  : themes.color.wfGrey100,
              }}
            />
          ),
          'data-testid': isActiveProfile
            ? 'myRFQQuotesOrders'
            : 'initiativeRFQQuotesOrders',
          onClick: () => {
            if (spacePath) {
              navigate(
                clientStore.currentPortal === 'INTERMEDIATOR'
                  ? 'workspace.intermediator.rfqs'
                  : 'workspace.rfqs',
                { spacePath },
              );
            }
          },
          isActive: isRFQQuotesOrders,
        }
      : undefined,
  ];
};

export const getProfileMenus = () => {
  const { navigate, route } = routerStore;
  const { profileSpace, signedInProfile } = clientStore;

  const isProfileSpace = isProfileSlug(clientStore.slugFromRouteOrSpace);

  const isSharedWithMeActiveItem =
    isProfileSpace && sharedWithMeRoutes.includes(route.name);

  const { sharedWithFiles, sharedWithProjects } = profileSpace || {};
  const sharedTotalCount =
    sharedWithFiles && sharedWithProjects
      ? sharedWithFiles.totalCount + sharedWithProjects.totalCount
      : null;

  const isActiveProfileSettings =
    isProfileSpace && route.name.startsWith('workspace.spaceSettings');

  const shouldShowUpgradeToPro = profileSpace && !profileSpace?.isPro;

  const spacePath = signedInProfile?.username;

  return [
    ...getCommonMenus({
      spacePath,
      statistics: profileSpace,
      isCurrentSpace: isProfileSpace,
    }),
    {
      label: (
        <MenuTitle
          text='Shared with me'
          totalCount={sharedTotalCount}
          className={isSharedWithMeActiveItem ? 'font-bold' : ''}
        />
      ),
      key: 'sharedWithMe',
      icon: (
        <SharedWithMeIcon
          style={{
            color: isSharedWithMeActiveItem
              ? themes.color.wfBlack
              : themes.color.wfGrey100,
          }}
        />
      ),
      'data-testid': 'sharedWithMe',
      onClick: () => {
        if (spacePath) {
          if (profileSpace?.inSpace?.id) {
            clientStore.SetActiveWorkspace(profileSpace.inSpace.id);
          }

          navigate('workspace.sharedWithProjects', {
            spacePath,
          });

          clientStore.UpdateProfileOrTeamWorkspace();
        }
      },
      isActive: isSharedWithMeActiveItem,
    },
    {
      icon: (
        <SettingsIcon
          style={{
            color: isActiveProfileSettings
              ? themes.color.wfBlack
              : themes.color.wfGrey100,
          }}
        />
      ),
      label: (
        <MenuTitle
          text='Profile Settings'
          className={isActiveProfileSettings ? 'font-bold' : ''}
        />
      ),
      key: 'settings',
      onClick: () => {
        if (spacePath) {
          navigate('workspace.spaceSettings.account', {
            spacePath,
          });
        }
      },
      'data-testid': 'profileSettings',
      isActive: isActiveProfileSettings,
    },
  ]
    .filter(Boolean)
    .map(item => ({
      ...item,
      style: {
        ...(item?.style || {}),
        ...commonMenuItemStyle,
        background: item.isActive ? themes.color.wfGrey100 : '',
      },
    }));
};

export const getInitiativeMenus = () => {
  const { navigate, route } = routerStore;
  const { activeInitiativeSpace } = clientStore;
  const { content } = activeInitiativeSpace;

  const isInitiativeSpace = isInitiativeSlug(clientStore.slugFromRouteOrSpace);

  const isActiveInitiativeSettings =
    isInitiativeSpace && route.name === 'workspace.spaceSettings.billing';

  const isActiveInitiativeMembers =
    isInitiativeSpace && route.name === 'workspace.spaceSettings.teams';

  const { canUpdate } = content || {};
  const spacePath = `+${content?.slug}`;

  const hasPermission = canUpdate;

  return [
    ...getCommonMenus({
      spacePath,
      statistics: activeInitiativeSpace,
      isCurrentSpace: isInitiativeSpace,
    }),
    hasPermission && {
      icon: (
        <div
          className={` w-6 h-6 border flex-center rounded-full ${
            isActiveInitiativeMembers ? ' border-wfBlack' : ' border-wfGrey100'
          }`}
        >
          <Icon
            size='xsmall'
            name='user_plus'
            style={{ width: 16, height: 16, color: themes.color.wfBlack }}
          />
        </div>
      ),
      label: (
        <MenuTitle
          text='Invite Members'
          className={isActiveInitiativeMembers ? 'font-bold' : ''}
        />
      ),
      key: 'InviteMembers',
      onClick: () => {
        if (spacePath) {
          navigate('workspace.spaceSettings.account', {
            spacePath,
          });
        }
      },
      'data-testid': 'workspace-inviteMembers',
      isActive: isActiveInitiativeMembers,
    },
    hasPermission && {
      icon: (
        <SettingsIcon
          style={{
            color: isActiveInitiativeSettings
              ? themes.color.wfBlack
              : themes.color.wfGrey100,
          }}
        />
      ),
      label: (
        <MenuTitle
          text='Team Settings'
          className={isActiveInitiativeSettings ? 'font-bold' : ''}
        />
      ),
      key: 'team settings',
      onClick: () => {
        if (spacePath) {
          navigate('workspace.spaceSettings.billing', {
            spacePath,
          });
        }
      },
      'data-testid': 'workspace-settings',
      isActive: isActiveInitiativeSettings,
    },
    activeInitiativeSpace.isTeamSubscription &&
      hasPermission && {
        icon: <UpgradeToBusinessIcon color={themes.color.wfGrey100} />,
        label: <MenuTitle text='Upgrade to Business' icon={<ArrowRight />} />,
        key: 'workspace.upgrade',
        'data-testid': 'upgradeToBusiness',
        onClick: () => {
          if (spacePath) {
            navigate('workspace.upgrade', {
              spacePath,
              productType: 'business',
            });
          }
        },
      },
  ]
    .filter(Boolean)
    .map(item => ({
      ...item,
      style: {
        ...(item?.style || {}),
        ...commonMenuItemStyle,
        background: item.isActive ? themes.color.wfGrey100 : '',
        '&:hover': {
          background: themes.color.wfGrey100,
        },
      },
    }));
};
